import { HStack, Skeleton } from "@chakra-ui/react"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import type { Card as UICard } from "@pathwright/ui/src/components/card/types"
import TextInput from "@pathwright/ui/src/components/form/form-text-input/TextInput"
import useDebouncedState from "@pathwright/ui/src/components/hooks/useDebouncedState"
import { usePaginator } from "@pathwright/utils"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import get from "lodash/get"
import React from "react"
import { Field, Form, Submit } from "react-connect-form-forked"
import {
  MentorGroupType,
  MentorGroupsDocument,
  useCreateMentorGroupMutation,
  useMentorGroupsQuery
} from "@pathwright/graphql-client/src/generated"
import { SimpleValueSelectInput } from "../../../lib/SelectInput/SelectInput"
import { flattenEdges } from "../../../utils/apollo"
import UpgradeMentorGroupsGate from "./UpgradeMentorGroupsGate"

interface MentorGroupFormProps {
  parentGroupId?: number | null
  onSave: () => void
}

export let MentorGroupForm: React.FC<MentorGroupFormProps> = ({
  parentGroupId,
  onSave
}) => {
  const { t } = useTranslate()
  const [search, setSearch] = useDebouncedState(300, null) as [
    string | null,
    (value: string) => void
  ]
  const [createMentorGroup] = useCreateMentorGroupMutation()

  const mentorGroupsQuery = useMentorGroupsQuery({
    variables: {
      seatLimitFilter: { neq: 1 },
      search: search || undefined
    }
  })

  const groups = mentorGroupsQuery.data
    ? flattenEdges(get(mentorGroupsQuery, "data.school.mentor_groups"))
    : []

  // Get total count from GraphQL response
  const totalGroups = get(
    mentorGroupsQuery,
    "data.school.mentor_groups.total",
    0
  )

  const { loadMore, hasMore, loadingMore } = usePaginator({
    query: mentorGroupsQuery,
    paginatorValue: mentorGroupsQuery.data?.school?.mentor_groups
  })

  // Show the parent field if:
  // 1. There are groups available (total > 0), OR
  // 2. User is actively searching (search is not null/empty)
  const shouldShowParentField = totalGroups > 0 || Boolean(search?.trim())

  const handleSubmit = async ({
    name,
    parent,
    type
  }: {
    name: string
    parent?: string | null
    type?: MentorGroupType
  }) => {
    const selectedParentId = parent ? parseInt(parent) : null

    await createMentorGroup({
      variables: {
        name,
        parent: selectedParentId,
        type
      },
      // Wait so onSave / goBack does not land on a still-empty list cache.
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: MentorGroupsDocument,
          variables: {
            parentId: { eq: selectedParentId },
            seatLimitFilter: { neq: 1 }
          }
        },
        {
          query: MentorGroupsDocument,
          variables: {
            parentId: { eq: selectedParentId },
            seatLimitFilter: { neq: 1 },
            topLevelOnly: true
          }
        },
        ...(!parentGroupId
          ? [
              {
                query: MentorGroupsDocument,
                variables: {
                  seatLimitFilter: { neq: 1 }
                }
              },
              {
                query: MentorGroupsDocument,
                variables: {
                  seatLimitFilter: { neq: 1 },
                  topLevelOnly: true
                }
              }
            ]
          : [])
      ]
    })
  }

  return (
    <UpgradeMentorGroupsGate>
      <Form
        initialValue={{
          parent: parentGroupId || null
        }}
        onSubmit={handleSubmit}
        onSubmitSuccess={onSave}
        style={{ width: "100%" }}
      >
        <CardBlock>
          <Field
            name="name"
            type="text"
            label={t("mentor_group.form.fields.name.label")}
            placeholder={t("mentor_group.form.fields.name.placeholder")}
            topLabel
            hideStatus
            component={TextInput}
            style={{ flexGrow: 1 }}
            required
          />
          {mentorGroupsQuery.loading && !shouldShowParentField ? (
            <div>
              <Skeleton height="20px" mb={2} />
              <Skeleton height="40px" />
            </div>
          ) : shouldShowParentField ? (
            <Field
              name="parent"
              type="select"
              label={t("mentor_group.form.fields.parent.label")}
              instructions={t("mentor_group.form.fields.parent.instructions")}
              topLabel
              hideStatus
              isSearchable
              component={SimpleValueSelectInput}
              placeholder={t("mentor_group.form.fields.select")}
              options={groups}
              getOptionLabel={(option: { name: string }) => option.name}
              getOptionValue={(option: { id: number }) => option.id}
              onMenuScrollToBottom={() => !loadingMore && hasMore && loadMore()}
              onInputChange={(val: string) => {
                setSearch(val)
                // return val for react-select to handle
                return val
              }}
            />
          ) : null}
        </CardBlock>
        <HStack w="100%" justify="center">
          <Submit styleType="primary" component={SubmitButton}>
            {t("mentor_group.form.create")}
          </Submit>
        </HStack>
      </Form>
    </UpgradeMentorGroupsGate>
  )
}

interface AddMentorGroupCardProps {
  card: UICard
  parentGroupId?: number | null
}

const AddMentorGroupCard: React.FC<AddMentorGroupCardProps> = ({
  card,
  parentGroupId
}) => {
  const { t } = useTranslate()
  const title = parentGroupId
    ? t("mentor_group.new_sub_group")
    : t("mentor_group.new_group")

  return (
    <Card card={card} title={title}>
      <MentorGroupForm parentGroupId={parentGroupId} onSave={card.onClose} />
    </Card>
  )
}

export default AddMentorGroupCard
